import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SignOutButton, useClerk } from "@clerk/react";
import { toast } from "sonner";
import { Check, LogOut, Plus, ShieldCheck } from "lucide-react";
import { ROLE_LABEL, roleLabel, useOrg } from "./org";

const Alerts = () => {
  const { api, membership } = useOrg();
  const [n, setN] = useState(null);
  const [email, setEmail] = useState("");
  useEffect(() => { api("/me/notifications").then((x) => { setN(x); setEmail(x.email || ""); }).catch(() => {}); }, [api]);
  if (!n) return null;
  const save = (body, msg) => api("/me/notifications", { method: "PATCH", body }).then((x) => { setN(x); setEmail(x.email || ""); toast.success(msg); }).catch((e) => toast.error(e.message));
  return (
    <section className="py-6 border-t border-line" data-testid="me-alerts">
      <h2 className="t-label">Care assignment emails</h2>
      <p className="t-body text-ink-soft mt-2">When someone is entrusted to your care, KNOWN emails you their name, why they need care, the next step and the latest shared note.</p>
      <label className="flex items-center gap-3 mt-4 cursor-pointer">
        <input type="checkbox" checked={!n.off} onChange={() => save({ email: n.email, off: !n.off }, n.off ? "Emails turned on" : "Emails turned off")} className="w-5 h-5 accent-[var(--moss)]" data-testid="me-alerts-toggle" />
        <span className="t-body">Email me when I'm assigned someone</span>
      </label>
      {!n.off && (
        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={n.default_email || "you@church.org"} className="field h-12 px-3 sm:flex-1 shrink-0" data-testid="me-alerts-email" />
          <button disabled={(email || null) === (n.email || null)} onClick={() => save({ email: email.trim() || null, off: false }, "Notification email saved")} className="btn btn-outline btn-sm" data-testid="me-alerts-save">Save</button>
        </div>
      )}
      {!n.off && <p className="t-meta mt-2" data-testid="me-alerts-dest">Sending to {n.email || n.default_email}{n.email ? "" : " (your sign-in email)"}. Leave empty to use your sign-in email.</p>}
      {membership.role !== "admin" && (
        <label className="flex items-center gap-3 mt-5 cursor-pointer">
          <input type="checkbox" checked={n.moments_digest} onChange={() => save({ email: n.email, off: n.off, moments_digest: !n.moments_digest }, n.moments_digest ? "Weekly Life Moments email turned off" : "Weekly Life Moments email turned on")} className="w-5 h-5 accent-[var(--moss)]" data-testid="me-moments-digest-toggle" />
          <span className="t-body">Email me a weekly list of upcoming birthdays and anniversaries</span>
        </label>
      )}
    </section>
  );
};

export default function Me() {
  const { me, membership, switchOrg } = useOrg();
  const { openUserProfile } = useClerk();
  const navigate = useNavigate();
  const [first, last] = me.user.name.split(" ");
  return (
    <div className="max-w-2xl" data-testid="me-screen">
      <div className="flex items-center gap-5">
        <span className="h-20 w-20 rounded-full bg-sunk text-ink-soft font-serif text-[30px] inline-flex items-center justify-center shrink-0">{first?.[0]}{last?.[0]}</span>
        <div className="min-w-0">
          <h1 className="t-name-xl [overflow-wrap:anywhere]" data-testid="me-name">{me.user.name}</h1>
          <p className="t-meta mt-1 [overflow-wrap:anywhere]" data-testid="me-email">{me.user.email}</p>
        </div>
      </div>
      <section className="mt-10 py-6 border-t border-line">
        <h2 className="t-label">Your church</h2>
        <p className="t-name mt-2" data-testid="me-church">{membership.org_name}</p>
        <p className="t-body text-ink-soft mt-1" data-testid="me-role">{roleLabel(membership, membership.role)}{membership.confidential_access ? " · Confidential Access" : ""}</p>
      </section>
      {me.memberships.length > 1 && (
        <section className="py-6 border-t border-line" data-testid="me-churches">
          <h2 className="t-label">Switch church</h2>
          <div className="rule-list mt-2">
            {me.memberships.map((m) => (
              <button key={m.org_id} onClick={() => { switchOrg(m.org_id); navigate("/"); }} className="w-full flex items-center justify-between gap-3 py-4 text-left" data-testid={`me-switch-${m.org_id}`}>
                <span><span className="block t-name-sm">{m.org_name}</span><span className="block t-meta">{roleLabel(m, m.role)}</span></span>
                {m.org_id === membership.org_id && <Check size={18} className="text-brand" />}
              </button>
            ))}
          </div>
        </section>
      )}
      <Alerts />
      <section className="py-6 border-t border-line flex flex-col sm:flex-row gap-3">
        <button onClick={() => openUserProfile()} className="btn btn-outline" data-testid="me-account-btn"><ShieldCheck size={18} /> Account & security</button>
        <button onClick={() => navigate("/new-church")} className="btn btn-quiet" data-testid="me-new-church-btn"><Plus size={18} /> Set up another church</button>
        <SignOutButton redirectUrl="/sign-in"><button className="btn btn-quiet sm:ml-auto" data-testid="me-signout-btn"><LogOut size={18} /> Sign out</button></SignOutButton>
      </section>
      <p className="t-meta">Account & security is where you change your email, password and connected Google account.</p>
    </div>
  );
}
